import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AMBIENT_SCENES } from '../media/ambientScenes';
import { demoItem } from '../media/demo';
import { isLite } from '../lib/devicePerf';
import { useDevice } from '../store/device';
import { useSettings, type MediaItem } from '../store/settings';
import { fitsScreen } from '../lib/orientation';
import { useSession } from '../store/session';
import { useCloudMedia } from './useCloudMedia';
import { isPortraitLayout, useEffectiveLayout } from './useLayout';

export type QueueEntry = { type: 'media'; item: MediaItem } | { type: 'ambient'; sceneIndex: number };

function shuffled<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j] as T, a[i] as T];
  }
  return a;
}

/**
 * Drives the slideshow: ordering (optionally shuffled), timing, and skipping items that
 * fail to load. Falls back to built-in ambient scenes when no media is configured.
 */
export function useMediaQueue(paused: boolean) {
  const { items: ownItems, intervalSec, shuffle, matchOrientation, demo, quality } = useSettings((s) => s.media);
  const cloudItems = useCloudMedia();
  const demoBase = useSession((s) => s.config?.demoMediaUrl ?? null);
  const effects = useDevice((s) => s.effects);
  const items = useMemo(() => [...ownItems, ...cloudItems], [ownItems, cloudItems]);
  const [failed, setFailed] = useState<Set<string>>(() => new Set());
  const screen = isPortraitLayout(useEffectiveLayout()) ? 'portrait' : 'landscape';
  // Items found (on load) not to match the screen's shape; reset when the shape changes.
  const [mismatched, setMismatched] = useState<Set<string>>(() => new Set());
  useEffect(() => setMismatched((m) => (m.size ? new Set() : m)), [screen, matchOrientation]);

  // Device files have no URL until restoreLocalMedia() rebuilds it after a reload.
  const playable = useMemo(() => {
    const ok = items.filter((i) => i.url && !failed.has(i.id));
    if (!matchOrientation) return ok;
    const shaped = ok.filter((i) => !mismatched.has(i.id) && fitsScreen(i.width, i.height, screen));
    // Never leave the screen empty: if nothing matches, show everything.
    return shaped.length ? shaped : ok;
  }, [items, failed, matchOrientation, mismatched, screen]);
  const order = useMemo<QueueEntry[]>(() => {
    if (!playable.length) {
      // Nothing of the user's own to show: the demo video (unless switched off or it failed).
      // A version that fails here is skipped for the next one down (see media/demo.ts).
      // Lite devices (TV sticks) get the Full HD demo: 4K/HDR plus effects can crash them.
      const d = demo ? demoItem(isLite(effects) ? '1080' : quality, demoBase, failed) : null;
      if (d) return [{ type: 'media', item: d }];
      return AMBIENT_SCENES.map((_, sceneIndex) => ({ type: 'ambient', sceneIndex }));
    }
    const list = playable.map((item) => ({ type: 'media' as const, item }));
    return shuffle ? shuffled(list) : list;
  }, [playable, shuffle, demo, quality, failed, demoBase, effects]);

  const [index, setIndex] = useState(0);
  const [cycle, setCycle] = useState(0); // bumps on every advance, used as animation key
  const safeIndex = order.length ? index % order.length : 0;
  const current = order[safeIndex] ?? null;

  const next = useCallback(() => {
    setIndex((i) => (order.length ? (i + 1) % order.length : 0));
    setCycle((c) => c + 1);
  }, [order.length]);

  const prev = useCallback(() => {
    setIndex((i) => (order.length ? (i - 1 + order.length) % order.length : 0));
    setCycle((c) => c + 1);
  }, [order.length]);

  // Videos advance on their own 'ended' when shorter than the interval; the timer is the cap.
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => {
    window.clearTimeout(timer.current);
    if (paused || order.length <= 1) return;
    timer.current = window.setTimeout(next, intervalSec * 1000);
    return () => window.clearTimeout(timer.current);
  }, [paused, intervalSec, next, cycle, order.length]);

  // Skip a broken item after a short pause, so a source where everything fails (expired
  // sign-in, provider outage) is walked at ~1 request/second rather than in a burst.
  const markFailed = useCallback(
    (id: string) => {
      setFailed((f) => new Set(f).add(id));
      window.setTimeout(next, 1000);
    },
    [next],
  );

  /** Called when a photo/video of unknown size loads: skip it if its shape doesn't fit. */
  const reportSize = useCallback(
    (id: string, width: number, height: number) => {
      if (!matchOrientation || fitsScreen(width, height, screen)) return;
      setMismatched((m) => new Set(m).add(id));
      next();
    },
    [matchOrientation, screen, next],
  );

  // Retry previously failed items when the list changes (e.g. user fixed a URL).
  // Returning the same Set when nothing failed keeps this from re-rendering on every change.
  useEffect(() => setFailed((f) => (f.size ? new Set() : f)), [items]);

  // Preload the next image so transitions never show a half-loaded frame.
  const upcoming = order[(safeIndex + 1) % Math.max(order.length, 1)];
  useEffect(() => {
    if (upcoming?.type === 'media' && upcoming.item.kind === 'image') {
      const img = new Image();
      img.decoding = 'async';
      img.src = upcoming.item.url;
    }
  }, [upcoming]);

  return { current, cycle, next, prev, markFailed, reportSize, total: order.length, intervalSec };
}
